// Viewer de una página del brain: contenido markdown (wikilinks navegables),
// metadatos y backlinks ("qué apunta aquí") — el grafo visible.

import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useBrainBacklinks, useBrainDelete, useBrainPage, useBrainWrite } from '@/hooks/useBrain'
import { BRAIN_TYPE_COLORS, BRAIN_TYPE_LABELS } from '@/types/brain'
import { BrainMarkdown } from './BrainMarkdown'

interface Props {
  slug: string | null
  onClose: () => void
  onOpenPage: (slug: string) => void
}

export function PageViewerSheet({ slug, onClose, onOpenPage }: Props) {
  const { data: page, isLoading } = useBrainPage(slug)
  const { data: backlinks } = useBrainBacklinks(page?.id ?? null)
  const del = useBrainDelete()
  const write = useBrainWrite()
  // Triage del inbox (mecanismo gbrain #6): las páginas no sincronizadas se
  // pueden editar en sitio — el backend ya valida propiedad/rol.
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  useEffect(() => { setEditing(false) }, [slug])
  const saveEdit = async () => {
    if (!page) return
    if (!draft.trim()) { toast.error('El contenido no puede quedar vacío'); return }
    try {
      await write.mutateAsync({ slug: page.slug, page_type: page.page_type, title: page.title, content_md: draft })
      toast.success('Página actualizada')
      setEditing(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo guardar (¿es tuya la página?)')
    }
  }

  const handleDelete = async () => {
    if (!page) return
    if (!window.confirm(`¿Borrar la página ${page.slug}? Esto no se puede deshacer.`)) return
    try {
      await del.mutateAsync(page.id)
      toast.success('Página borrada')
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo borrar (solo el creador o un admin)')
    }
  }

  return (
    <Sheet open={!!slug} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent className="w-full overflow-hidden sm:max-w-xl">
        {/* Radix Dialog a11y: every open state needs a SheetTitle */}
        {(isLoading || (slug && !page)) && (
          <SheetHeader className="p-6">
            <SheetTitle className="text-left font-mono text-sm">{slug ?? ''}</SheetTitle>
            <SheetDescription className="text-left">
              {isLoading ? 'Cargando…' : 'Esta página todavía no existe en el cerebro.'}
            </SheetDescription>
          </SheetHeader>
        )}
        {page && (
          <div className="flex h-full flex-col p-5">
            <SheetHeader className="pr-8">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className={BRAIN_TYPE_COLORS[page.page_type]}>
                  {BRAIN_TYPE_LABELS[page.page_type]}
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">{page.slug}</span>
              </div>
              <SheetTitle className="text-left">{page.title}</SheetTitle>
              <SheetDescription className="text-left">
                Fuente: {page.source}
                {page.source_ref ? ` · ${page.source_ref}` : ''} · Actualizada{' '}
                {format(new Date(page.updated_at), 'yyyy-MM-dd HH:mm')}
              </SheetDescription>
            </SheetHeader>
            <ScrollArea className="mt-3 flex-1 pr-3">
              {editing ? (
                <div className="space-y-2">
                  <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} className="font-mono text-xs" />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancelar</Button>
                    <Button size="sm" onClick={saveEdit} disabled={write.isPending}>
                      {write.isPending ? 'Guardando…' : 'Guardar'}
                    </Button>
                  </div>
                </div>
              ) : (
                <BrainMarkdown content={page.content_md || '_(página sin contenido)_'} onOpenPage={onOpenPage} />
              )}
              {backlinks && backlinks.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Conectado desde ({backlinks.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5 pb-4">
                    {backlinks.map((b) => (
                      <button
                        key={`${b.page.id}-${b.link_type}`}
                        type="button"
                        onClick={() => onOpenPage(b.page.slug)}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted"
                        title={b.link_type}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${b.link_type === 'wikilink' ? 'bg-teal-500' : 'bg-muted-foreground'}`} />
                        {b.page.title}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </ScrollArea>
            <div className="flex items-center justify-end gap-3 border-t border-border/40 pt-3">
              {['gong', 'salesforce', 'fireflies', 'slack'].includes(page.source) ? (
                <span className="text-xs text-muted-foreground">
                  Página sincronizada — borrarla la haría reaparecer en el próximo sync; ajusta el
                  matching en la página de la cuenta.
                </span>
              ) : page.source === 'deck' ? (
                <span className="text-xs text-muted-foreground">
                  Página curada del deck — se actualiza re-corriendo el seed cuando cambien los números.
                </span>
              ) : (
                <>
                  {!editing && (
                    <Button variant="ghost" size="sm" onClick={() => { setDraft(page.content_md ?? ''); setEditing(true) }}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDelete} disabled={del.isPending}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Borrar página
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
